import { isAnalyticsGranted } from "../../core/consent";
import { track } from "../../core/analytics/ga4";

export type MarketingEventPayload = Record<string, unknown>;

const PLAYGROUND_HOST = "playground.fidava-pt.com";

/**
 * Map a site CTA to the GA4 event the "Plano de eventos GA4" expects, from
 * the link it points at: Calendly / mailto → contact_click, the playground →
 * playground_click, the brochure → brochure_download, the wizard →
 * start_click (plan cards fire plan_cta_click themselves). Every section
 * already reports its CTAs through recordCta, so deriving here covers them
 * all without a handler per button.
 */
export const trackSiteCta = (href: string | undefined, location: string): void => {
    if (!href) return;
    if (href.startsWith("mailto:")) {
        track("contact_click", { method: "email", location });
    } else if (/calendly\.com/i.test(href)) {
        track("contact_click", { method: "calendly", location });
    } else if (href.includes(PLAYGROUND_HOST)) {
        track("playground_click", { location });
    } else if (/\/(fidava|converge)\/brochure/.test(href)) {
        track("brochure_download", { location });
    } else if (href.startsWith("/startupWizard")) {
        track("start_click", { location });
    }
};

const DEFAULT_EVENT_SOURCE = "site";

const ensureDataLayer = (): any[] | undefined => {
    if (typeof window === "undefined") {
        return undefined;
    }
    if (!Array.isArray((window as any).dataLayer)) {
        (window as any).dataLayer = [];
    }
    return (window as any).dataLayer;
};

export const pushMarketingEvent = (
    eventName: string,
    payload: MarketingEventPayload = {},
    opts: { source?: string } = {}
): void => {
    // Marketing/analytics dataLayer pushes require prior analytics consent.
    if (!isAnalyticsGranted()) {
        return;
    }

    const dataLayer = ensureDataLayer();
    if (!dataLayer) {
        return;
    }

    const source = opts.source ?? DEFAULT_EVENT_SOURCE;
    dataLayer.push({
        event: eventName,
        source,
        timestamp: new Date().toISOString(),
        ...payload
    });
};

export const recordPageView = (pageName: string): void => {
    pushMarketingEvent("page_view", { page_name: pageName });
};

export const recordCta = (ctaId: string, context: MarketingEventPayload = {}): void => {
    pushMarketingEvent("cta_click", { cta_id: ctaId, ...context });
    // Plan cards report plan_cta_click with plan + seats themselves.
    if (!ctaId.startsWith("cta-final-") || ctaId.endsWith("-enterprise")) {
        trackSiteCta(typeof context.href === "string" ? context.href : undefined, String(context.section ?? ctaId));
    }
};

export const recordSectionImpression = (sectionId: string, context: MarketingEventPayload = {}): void => {
    pushMarketingEvent("section_impression", { section_id: sectionId, ...context });
};
